import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BoardModel, BoardNode, NodeKind } from '@core/types';
import { STAGE_LABEL } from '@core/derived';
import './Board.css';

const COL_W = 156;
const LANE_H = 88;
const PAD_X = 96;
const NODE_R = 21;
const MILESTONE_R = 27;

/**
 * Per-kind glyphs, drawn as SVG paths on a 24×24 grid centred on the origin.
 * Vector rather than raster: the board scales to any viewport, the whole icon
 * set costs under 3 kB, and nothing has to be exported from a design tool.
 */
const GLYPHS: Record<NodeKind, string> = {
  start: 'M-5 -6 L6 0 L-5 6 Z',
  milestone: 'M0 -7 L2 -2 L7 -2 L3 1.5 L4.5 7 L0 3.8 L-4.5 7 L-3 1.5 L-7 -2 L-2 -2 Z',
  education: 'M-8 -2 L0 -6 L8 -2 L0 2 Z M5 0 V5 M-5 0 V5',
  career: 'M-7 -4 H7 V6 H-7 Z M-2.5 -4 V-6.5 H2.5 V-4',
  relationship: 'M-3.5 -4.5 a3.2 3.2 0 1 1 0 6.4 a3.2 3.2 0 1 1 0 -6.4 M3.5 -4.5 a3.2 3.2 0 1 1 0 6.4 a3.2 3.2 0 1 1 0 -6.4 M-7 6.5 q3.5 -3 7 0 M0 6.5 q3.5 -3 7 0',
  family: 'M-6 6 V0 a2.4 2.4 0 0 1 4.8 0 V6 M1.2 6 V-1.5 a2.2 2.2 0 0 1 4.4 0 V6 M-3.6 -3.4 a1.9 1.9 0 1 1 0 0.1 M3.4 -4.6 a1.7 1.7 0 1 1 0 0.1',
  finance: 'M0 -7 V7 M-4 -3.6 q0 -2.6 4 -2.6 q4 0 4 2.6 q0 2.4 -4 3 q-4 0.6 -4 3.2 q0 2.6 4 2.6 q4 0 4 -2.6',
  investment: 'M-7 5 L-2 -1 L2 2 L7 -5 M3.4 -5 H7 V-1.4',
  business: 'M-7 -1 H7 V6 H-7 Z M-2.6 -1 V-4 H2.6 V-1 M-7 2 H7',
  leisure: 'M0 -6 a6 6 0 1 0 0.01 0 M0 -3 V0 L2.6 1.6',
  health: 'M-7 0 H-3.6 L-1.8 -4 L1 4 L2.8 0 H7',
  event: 'M0 -7 L7 0 L0 7 L-7 0 Z',
  opportunity: 'M-6 1 a6 6 0 1 1 12 0 M-2.2 5.6 H2.2 M-1.4 7.6 H1.4',
  risk: 'M0 -7 L7.5 6 H-7.5 Z M0 -2.4 V1.6 M0 3.4 V3.5',
  recovery: 'M-6 1 a6 6 0 1 0 2.4 -4.8 M-6 -4.6 V-1 H-2.4',
  crossroads: 'M0 -7 V7 M-7 0 H7',
  retirement: 'M-7 6 V-2 L0 -6.5 L7 -2 V6 Z M-2.6 6 V1 H2.6 V6',
  legacy: 'M-5.5 -6 H5.5 V6 H-5.5 Z M-2.6 -2.6 H2.6 M-2.6 0.6 H2.6',
  end: 'M0 -7 a7 7 0 1 0 0.01 0 M-4.6 0 H4.6',
};

interface Props {
  board: BoardModel;
  currentId: string;
  visited: string[];
  available: BoardNode[];
  age: number;
  onSelect: (nodeId: string) => void;
  interactive: boolean;
}

const nodeX = (node: BoardNode): number => PAD_X + node.column * COL_W;
const nodeY = (node: BoardNode, midY: number): number => midY + node.lane * LANE_H;

export function Board({ board, currentId, visited, available, age, onSelect, interactive }: Props) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState({ w: 1200, h: 520 });
  const [pan, setPan] = useState(0);
  const [userPanning, setUserPanning] = useState(false);
  const drag = useRef<{ startX: number; startPan: number; moved: boolean; captured: boolean } | null>(null);

  const lanes = useMemo(() => {
    let min = 0;
    let max = 0;
    for (const node of Object.values(board.nodes)) {
      min = Math.min(min, node.lane);
      max = Math.max(max, node.lane);
    }
    return { min, max };
  }, [board]);

  const boardWidth = PAD_X * 2 + (board.columns - 1) * COL_W;
  const boardHeight = (lanes.max - lanes.min) * LANE_H + LANE_H * 2.1;
  const midY = -lanes.min * LANE_H + LANE_H * 1.05;

  const maxPan = Math.max(0, boardWidth - viewport.w);

  /* Keep the token comfortably in view whenever it moves. */
  const current = board.nodes[currentId];
  useEffect(() => {
    if (!current || userPanning) return;
    const target = Math.max(0, Math.min(maxPan, nodeX(current) - viewport.w * 0.34));
    setPan(target);
  }, [current, currentId, maxPan, viewport.w, userPanning]);

  useEffect(() => {
    const element = wrapRef.current;
    if (!element) return;
    const observer = new ResizeObserver(() => {
      setViewport({ w: element.clientWidth, h: element.clientHeight });
    });
    observer.observe(element);
    setViewport({ w: element.clientWidth, h: element.clientHeight });
    return () => observer.disconnect();
  }, []);

  const clampPan = useCallback((value: number) => Math.max(0, Math.min(maxPan, value)), [maxPan]);

  const onPointerDown = (event: React.PointerEvent) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    // Deliberately *not* capturing the pointer here. Capturing on pointerdown
    // retargets the subsequent `click` event to the capture element, which
    // silently prevented every board step button from ever firing. Capture is
    // taken only once the gesture is unambiguously a drag.
    drag.current = { startX: event.clientX, startPan: pan, moved: false, captured: false };
  };

  const onPointerMove = (event: React.PointerEvent) => {
    const d = drag.current;
    if (!d) return;
    const dx = event.clientX - d.startX;
    if (!d.moved && Math.abs(dx) > 5) {
      d.moved = true;
      setUserPanning(true);
      try {
        (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
        d.captured = true;
      } catch {
        // Capture is an optimisation, not a requirement.
      }
    }
    if (d.moved) setPan(clampPan(d.startPan - dx));
  };

  const onPointerUp = (event: React.PointerEvent) => {
    const d = drag.current;
    if (d?.captured) {
      try {
        (event.currentTarget as HTMLElement).releasePointerCapture(event.pointerId);
      } catch {
        // Already released; harmless.
      }
    }
    if (d?.moved) {
      // Re-centre on the token shortly after the player stops dragging, but
      // keep the flag set long enough that the click guard still sees it.
      window.setTimeout(() => {
        drag.current = null;
        setUserPanning(false);
      }, 2600);
    } else {
      drag.current = null;
    }
  };

  const onWheel = (event: React.WheelEvent) => {
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (delta === 0) return;
    setUserPanning(true);
    setPan((p) => clampPan(p + delta));
    window.clearTimeout(wheelTimer.current);
    wheelTimer.current = window.setTimeout(() => setUserPanning(false), 2600);
  };
  const wheelTimer = useRef<number>(0);

  const availableIds = useMemo(() => new Set(available.map((n) => n.id)), [available]);
  const visitedIds = useMemo(() => new Set(visited), [visited]);

  /* Stage bands, drawn behind the path as quiet chapter markers. */
  const stageBands = useMemo(() => {
    const bands: { stage: BoardNode['stage']; from: number; to: number }[] = [];
    for (const id of board.order) {
      const node = board.nodes[id];
      if (!node) continue;
      const last = bands[bands.length - 1];
      if (last && last.stage === node.stage) last.to = node.column;
      else bands.push({ stage: node.stage, from: node.column, to: node.column });
    }
    return bands;
  }, [board]);

  const edges = useMemo(() => {
    const out: { id: string; d: string; live: boolean; past: boolean }[] = [];
    for (const id of board.order) {
      const from = board.nodes[id];
      if (!from) continue;
      for (const nextId of from.next) {
        const to = board.nodes[nextId];
        if (!to) continue;
        const x1 = nodeX(from);
        const y1 = nodeY(from, midY);
        const x2 = nodeX(to);
        const y2 = nodeY(to, midY);
        const cx = (x2 - x1) * 0.5;
        out.push({
          id: `${id}->${nextId}`,
          d: `M${x1} ${y1} C ${x1 + cx} ${y1}, ${x2 - cx} ${y2}, ${x2} ${y2}`,
          live: id === currentId && availableIds.has(nextId),
          past: visitedIds.has(id) && visitedIds.has(nextId),
        });
      }
    }
    return out;
  }, [board, currentId, availableIds, visitedIds, midY]);

  return (
    <div
      className="board"
      ref={wrapRef}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={onWheel}
      role="application"
      aria-label="Life board"
    >
      <svg
        className="board__svg"
        width={boardWidth}
        height={boardHeight}
        viewBox={`0 0 ${boardWidth} ${boardHeight}`}
        style={{ transform: `translateX(${-pan}px)` }}
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="liveEdge" x1="0" x2="1">
            <stop offset="0%" stopColor="var(--c-accent)" stopOpacity="0.15" />
            <stop offset="100%" stopColor="var(--c-accent)" stopOpacity="0.9" />
          </linearGradient>
          <radialGradient id="tokenGlow">
            <stop offset="0%" stopColor="var(--c-accent)" stopOpacity="0.5" />
            <stop offset="100%" stopColor="var(--c-accent)" stopOpacity="0" />
          </radialGradient>
        </defs>

        {stageBands.map((band, index) => {
          const x = PAD_X + band.from * COL_W - COL_W * 0.5;
          const w = (band.to - band.from + 1) * COL_W;
          return (
            <g key={`${band.stage}-${index}`}>
              {index % 2 === 1 && (
                <rect x={x} y={0} width={w} height={boardHeight} className="board__band" />
              )}
              <text x={x + 14} y={26} className="board__stageLabel">
                {STAGE_LABEL[band.stage]}
              </text>
            </g>
          );
        })}

        {edges.map((edge) => (
          <path
            key={edge.id}
            d={edge.d}
            className={`board__edge${edge.past ? ' board__edge--past' : ''}${edge.live ? ' board__edge--live' : ''}`}
            stroke={edge.live ? 'url(#liveEdge)' : undefined}
          />
        ))}

        {board.order.map((id) => {
          const node = board.nodes[id];
          if (!node) return null;
          const x = nodeX(node);
          const y = nodeY(node, midY);
          const isCurrent = id === currentId;
          const isAvailable = availableIds.has(id);
          const isVisited = visitedIds.has(id);
          const r = node.milestone ? MILESTONE_R : NODE_R;
          const classes = [
            'board__node',
            node.milestone ? 'board__node--milestone' : '',
            isVisited ? 'board__node--visited' : '',
            isCurrent ? 'board__node--current' : '',
            isAvailable ? 'board__node--available' : '',
            !isVisited && !isAvailable && !isCurrent ? 'board__node--future' : '',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <g key={id} className={classes} transform={`translate(${x} ${y})`}>
              {isAvailable && <circle r={r + 12} className="board__halo" />}
              <circle r={r} className="board__disc" />
              <path d={GLYPHS[node.kind]} className="board__glyph" />
              <text y={r + 20} className="board__label">
                {node.title}
              </text>
              {node.yearCost > 1 && isAvailable && (
                <text y={r + 34} className="board__cost">
                  +{node.yearCost} yrs
                </text>
              )}
            </g>
          );
        })}

        {current && (
          <g
            className="board__token"
            transform={`translate(${nodeX(current)} ${nodeY(current, midY)})`}
          >
            <circle r={46} fill="url(#tokenGlow)" />
            <circle r={13} className="board__tokenRing" />
            <circle r={7} className="board__tokenCore" />
            <text y={-26} className="board__tokenAge numeral">
              {age}
            </text>
          </g>
        )}
      </svg>

      {/* Clickable targets live outside the transformed SVG so they stay
          reliable under touch, and remain reachable by keyboard. */}
      <div className="board__hits" style={{ transform: `translateX(${-pan}px)` }}>
        {available.map((node) => (
          <button
            key={node.id}
            type="button"
            className="board__hit"
            style={{ left: nodeX(node) - 34, top: nodeY(node, midY) - 34 }}
            onClick={() => {
              if (!drag.current?.moved && interactive) onSelect(node.id);
            }}
            disabled={!interactive}
            aria-label={`Go to ${node.title}${node.subtitle ? `. ${node.subtitle}` : ''}. Costs ${node.yearCost} year${node.yearCost === 1 ? '' : 's'}.`}
          >
            <span className="sr-only">{node.title}</span>
          </button>
        ))}
      </div>

      {maxPan > 0 && (
        <div className="board__scrollHint" aria-hidden="true">
          <div
            className="board__scrollThumb"
            style={{
              width: `${(viewport.w / boardWidth) * 100}%`,
              left: `${(pan / boardWidth) * 100}%`,
            }}
          />
        </div>
      )}
    </div>
  );
}
